// @givmo/board — a closed session's minutes out for the signatures of the directors who were in it
// (CBG-RC10, D-CBG-24): the tolerant reads of what the backend serves about the request, and nothing
// more (backend `board_minutes.md` §S10).
//
// ══ THE SERVER DECIDES WHO SIGNS, AND WHERE EACH SIGNATURE STANDS ═══════════════════════════════
//
// Who was asked (every seat the session recorded that was a current director at the ask, recused
// inside it or not), who wasn't and why, whether each signature names the CURRENT bytes, whether the
// session's recorded seats changed since the ask, and whether the last signature approved them: every
// one of those is decided once, server-side, under the minutes' lock. This file reads the answers and
// re-derives none of them — a second count here could call minutes approved that aren't, or ask a
// director to sign who wasn't asked.
//
// A read carrying none of these fields (an older backend, or minutes with no request out) gives
// `null`, and the pages then offer nothing that depends on a request.

import type {
  BoardDirectorApprovalRequestOut,
  BoardMinutesOut,
  BoardMinutesSignatureOut,
  OrgBoardRecordOut,
} from '@givmo/api-client';
import type { NotAskedPerson, NotAskedReason } from './minutes-signatures-words';
import { deliveryOf, type SeatDelivery } from '../consents/consents-decode';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function count(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;
}

/** Where one asked director's signature stands on the CURRENT bytes (backend `standing`). */
export type SignerStanding = 'current' | 'earlier_version' | null;

function standing(value: unknown): SignerStanding {
  return value === 'current' || value === 'earlier_version' ? value : null;
}

/** One asked director, as the officer's Signatures card lists them (frame `c2`). */
export interface AskedSigner {
  boardMemberId: string;
  name: string | null;
  signed: SignerStanding;
  signedAt: string | null;
  captureMethod: string | null;
  /**
   * What the ask's email did for this director (CBG-RC10-MAIL, backend §S17.5), read as a consent
   * seat's is; `null` for an ask made before the email existed, and the page then says nothing.
   */
  delivery: SeatDelivery | null;
}

/** The open request on these draft minutes, as the officer's page reads it. */
export interface ApprovalSignaturesRequest {
  requestId: string;
  signerCount: number;
  signedCount: number;
  signers: AskedSigner[];
  notAsked: NotAskedPerson[];
  sessionChanged: boolean;
}

/**
 * `approval_signatures` off the officer's minutes read: the request out on these draft minutes, or
 * null when none is (or the read carried no such field). Never throws; a signer or a person not asked
 * with no id or no name the page could show is left out, never shown as an id.
 */
export function approvalSignaturesRequest(
  minutes: Pick<BoardMinutesOut, 'approval_signatures'> | null | undefined,
): ApprovalSignaturesRequest | null {
  const raw: unknown = minutes?.approval_signatures;
  if (!isRecord(raw)) return null;
  const requestId = text(raw.request_id);
  const signerCount = count(raw.signer_count);
  const signedCount = count(raw.signed_count);
  if (requestId === null || signerCount === null || signedCount === null) return null;
  const signers: AskedSigner[] = [];
  for (const entry of Array.isArray(raw.signers) ? raw.signers : []) {
    if (!isRecord(entry)) continue;
    const id = text(entry.board_member_id);
    if (id === null || signers.some((signer) => signer.boardMemberId === id)) continue;
    signers.push({
      boardMemberId: id,
      name: text(entry.display_name),
      signed: standing(entry.signed),
      signedAt: text(entry.signed_at),
      captureMethod: text(entry.capture_method),
      delivery: deliveryOf(entry.delivery),
    });
  }
  const notAsked: NotAskedPerson[] = [];
  for (const entry of Array.isArray(raw.not_asked) ? raw.not_asked : []) {
    if (!isRecord(entry)) continue;
    const name = text(entry.display_name);
    const reason = entry.reason;
    if (name === null || (reason !== 'stepped_out' && reason !== 'not_a_director')) continue;
    notAsked.push({ name, reason: reason as NotAskedReason });
  }
  return {
    requestId,
    signerCount,
    signedCount,
    signers,
    notAsked,
    sessionChanged: raw.session_changed === true,
  };
}

/** One withdrawn request for signatures on these minutes, as the minutes page lists it. */
export interface WithdrawnRequest {
  requestId: string;
  requestedAt: string;
  requestedByName: string | null;
  withdrawnAt: string;
  withdrawnByName: string | null;
  reason: string;
}

/**
 * The WITHDRAWN requests off the officer's minutes read (`signature_requests`, CBG-RC10-WITHDRAWN,
 * backend §S18.1), newest first as the server orders them. The open request and the approving one are
 * on the page already (the out line, the approved line), so only a withdrawn one is listed (the
 * orchestrator's W-C3). A row missing its dates or its reason is left out rather than shown in part;
 * a name that didn't resolve stays `null`, for the page's approved words. Never throws.
 */
export function withdrawnRequests(
  minutes: Pick<BoardMinutesOut, 'signature_requests'> | null | undefined,
): WithdrawnRequest[] {
  const rows: unknown = minutes?.signature_requests;
  if (!Array.isArray(rows)) return [];
  const withdrawn: WithdrawnRequest[] = [];
  for (const entry of rows) {
    if (!isRecord(entry) || entry.status !== 'withdrawn') continue;
    const requestId = text(entry.request_id);
    const requestedAt = text(entry.requested_at);
    const withdrawnAt = text(entry.withdrawn_at);
    const reason = text(entry.withdrawal_reason);
    if (requestId === null || requestedAt === null || withdrawnAt === null || reason === null) {
      continue;
    }
    withdrawn.push({
      requestId,
      requestedAt,
      requestedByName: text(entry.requested_by_display_name),
      withdrawnAt,
      withdrawnByName: text(entry.withdrawn_by_display_name),
      reason,
    });
  }
  return withdrawn;
}

/** The asked directors an officer may still enter a signature for: none yet on the current bytes. */
export function unsignedSigners(request: ApprovalSignaturesRequest): AskedSigner[] {
  return request.signers.filter((signer) => signer.signed !== 'current');
}

/** How many signatures approved these minutes, when signatures did (`approved_by_signatures`). */
export interface ApprovedBySignatures {
  requestId: string;
  signedCount: number | null;
}

/**
 * The request whose signatures approved these minutes, off the minutes read or the record read
 * (`approved_by_signatures`), or null when they were approved another way, not yet, or the read
 * carried no such field.
 */
export function approvedBySignatures(
  read:
    | Pick<BoardMinutesOut, 'approved_by_signatures'>
    | Pick<OrgBoardRecordOut, 'approved_by_signatures'>
    | null
    | undefined,
): ApprovedBySignatures | null {
  const raw: unknown = read?.approved_by_signatures;
  if (!isRecord(raw)) return null;
  const requestId = text(raw.request_id);
  if (requestId === null) return null;
  return { requestId, signedCount: count(raw.signed_count) };
}

/** Whether the record read says these minutes are out for signatures (`out_for_signatures`). */
export function isOutForSignatures(
  record: { out_for_signatures?: boolean | null } | null | undefined,
): boolean {
  return record?.out_for_signatures === true;
}

/** Whether this after-approval list row is one of the signatures that approved them. */
export function signedToApprove(signature: BoardMinutesSignatureOut): boolean {
  return signature.to_approve === true;
}

// ── The director's side (frame d5) ───────────────────────────────────────────────────────────────

/** The request as an asked director reads it on the minutes' record page. */
export interface DirectorApprovalRequest {
  signerCount: number;
  signedCount: number;
  youSigned: SignerStanding;
  youSignedAt: string | null;
  sessionChanged: boolean;
}

/**
 * `approval_request` off the director's record read: present only for a director the open request
 * asked, by custody (the backend's §S5 step 2), else null. Never throws.
 */
export function directorApprovalRequest(
  record: { approval_request?: BoardDirectorApprovalRequestOut | null } | null | undefined,
): DirectorApprovalRequest | null {
  const raw: unknown = record?.approval_request;
  if (!isRecord(raw)) return null;
  const signerCount = count(raw.signer_count);
  const signedCount = count(raw.signed_count);
  if (signerCount === null || signedCount === null) return null;
  return {
    signerCount,
    signedCount,
    youSigned: standing(raw.you_signed),
    youSignedAt: text(raw.you_signed_at),
    sessionChanged: raw.session_changed === true,
  };
}

/** One closed session's minutes this director was asked to sign, from the attention list. */
export interface ApprovalSignatureAwaiting {
  recordId: string;
  meetingId: string | null;
  meetingTitle: string | null;
  meetingStartsAt: string | null;
  meetingTimeZone: string | null;
  purposeLabel: string | null;
  signedEarlierVersion: boolean;
}

/**
 * The `minutes_approval_signature` entries of the attention list (the backend's §S9): only the asked
 * directors' who haven't signed the current bytes, and only while the window is open — the backend
 * applies both, and nothing here re-derives them. Public facts only: the meeting and the session's
 * general purpose, never the minutes' own title.
 */
export function approvalSignaturesAwaiting(items: unknown): ApprovalSignatureAwaiting[] {
  if (!Array.isArray(items)) return [];
  const awaiting: ApprovalSignatureAwaiting[] = [];
  for (const raw of items) {
    if (!isRecord(raw) || raw.kind !== 'minutes_approval_signature') continue;
    const recordId = text(raw.reference_id);
    if (recordId === null || awaiting.some((item) => item.recordId === recordId)) continue;
    awaiting.push({
      recordId,
      meetingId: text(raw.meeting_id),
      meetingTitle: text(raw.meeting_title),
      meetingStartsAt: text(raw.meeting_starts_at),
      meetingTimeZone: text(raw.meeting_time_zone),
      purposeLabel: text(raw.purpose_label),
      signedEarlierVersion: raw.signed_earlier_version === true,
    });
  }
  return awaiting;
}
